// Housekeeping · gestión (950): indicadores para la gerencia (con reporte
// diario imprimible), trabajo fuera de la jornada → propuesta a Nómina, bono
// por productividad y calidad, y hoja de QR para las puertas.
// Líneas rojas (dictamen laboral): los indicadores son de gestión, sin ranking
// punitivo; las horas fuera de jornada las decide una persona; el bono solo suma.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { BarChart3, Clock, Gift, Printer, QrCode, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, fmtFecha, hoyISO, hotelRpc, money, QK, sumarDias, etiquetaPiso, useDialogoEnLinea } from './shared';

type Sub = 'indicadores' | 'horas' | 'bono' | 'qr';
interface Kpis {
  estandar: { salida: number; ocupada: number; libre: number };
  totales: { tareas: number; terminadas: number; creditos: number; min_salida: number | null; min_ocupada: number | null; min_libre: number | null;
    inspecciones: number; aprobadas_primera_pct: number | null; relimpiezas: number; no_molestar: number; averias: number; rapidas: number };
  por_dia: { fecha: string; tareas: number; terminadas: number; creditos: number; min_salida: number | null; relimpiezas: number; ultima_lista_llegada: string | null }[];
  por_camarera: { nombre: string; tareas: number; terminadas: number; creditos: number; min_por_credito: number | null; inspecciones: number;
    aprobadas_primera_pct: number | null; relimpiezas: number; observaciones: number }[];
  defectos: { texto: string; veces: number }[];
}
interface HoraFila {
  camarera_id: string; camarera: string; fecha: string; tareas: number; primera: string; ultima: string; entrada: string | null; salida: string | null;
  turno: string | null; turno_horario: string | null; laborable: boolean | null; minutos: number; situacion: 'sin_marcacion' | 'fuera_de_jornada';
  tipo_sugerido: string; horas_sugeridas: number; decision: string | null; horas_enviadas: number | null; motivo_decision: string | null;
}
interface BonoResp {
  mes: string; activo: boolean; meta: number; valor: number; aprobacion_min: number;
  camareras: { camarera_id: string; nombre: string; vinculada: boolean; creditos: number; tareas: number; inspecciones: number; aprobacion_pct: number | null;
    cumple_calidad: boolean; bono: number; enviado: { monto: number; fecha: string } | null }[];
}

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const vsEstandar = (v: number | null, est: number) => (v == null ? '—' : `${v} min${v > est * 1.2 ? ' ▲' : ''}`);

function imprimirHtml(titulo: string, cuerpo: string) {
  const w = window.open('', '_blank');
  if (!w) { toast.error('El navegador bloqueó la ventana de impresión'); return; }
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(titulo)}</title>
    <style>body{font-family:system-ui,sans-serif;margin:16px;color:#111}h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;margin:16px 0 4px}
    table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #ccc;padding:3px 6px;text-align:left}td.n{text-align:right}
    .nota{font-size:11px;color:#555}.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
    .qr{border:1px dashed #999;padding:8px;text-align:center;page-break-inside:avoid}.qr b{font-size:28px;display:block}.qr svg{width:150px;height:150px}
    @media print{button{display:none}}</style></head><body>${cuerpo}
    <p><button onclick="window.print()">Imprimir</button></p></body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
}

export function GestionHk() {
  const [sub, setSub] = useState<Sub>('indicadores');
  const tabs: [Sub, string, typeof Clock][] = [['indicadores', 'Indicadores', BarChart3], ['horas', 'Horas → Nómina', Clock], ['bono', 'Bono', Gift], ['qr', 'QR de puertas', QrCode]];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1">
        {tabs.map(([k, l, I]) => (
          <button key={k} type="button" onClick={() => setSub(k)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm ${sub === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            <I className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>
      {sub === 'indicadores' && <Indicadores />}
      {sub === 'horas' && <Horas />}
      {sub === 'bono' && <Bono />}
      {sub === 'qr' && <QrPuertas />}
    </div>
  );
}

// ── Indicadores ────────────────────────────────────────────────────────────
function Indicadores() {
  const [desde, setDesde] = useState(sumarDias(hoyISO(), -29));
  const [hasta, setHasta] = useState(hoyISO());
  const { data: k, isFetching } = useQuery<Kpis>({ queryKey: [QK, 'hk-kpis', desde, hasta], queryFn: () => hotelRpc('hk_kpis', { _desde: desde, _hasta: hasta }) });
  const reporteDia = async () => {
    try {
      const d = await hotelRpc<Kpis>('hk_kpis', { _desde: hasta, _hasta: hasta });
      const t = d.totales;
      imprimirHtml(`Housekeeping ${hasta}`, `<h1>Reporte diario de housekeeping · ${esc(fmtFecha(hasta))}</h1>
        <table><tr><th>Habitaciones limpiadas</th><td class="n">${t.terminadas} de ${t.tareas}</td><th>Créditos</th><td class="n">${t.creditos}</td></tr>
        <tr><th>Minutos salida (estándar ${d.estandar.salida})</th><td class="n">${t.min_salida ?? '—'}</td><th>Minutos ocupada (estándar ${d.estandar.ocupada})</th><td class="n">${t.min_ocupada ?? '—'}</td></tr>
        <tr><th>Inspecciones</th><td class="n">${t.inspecciones}</td><th>Aprobadas a la primera</th><td class="n">${t.aprobadas_primera_pct ?? '—'} %</td></tr>
        <tr><th>Re-limpiezas</th><td class="n">${t.relimpiezas}</td><th>No molestar</th><td class="n">${t.no_molestar}</td></tr>
        <tr><th>Averías reportadas</th><td class="n">${t.averias}</td><th>Última habitación lista con llegada</th><td class="n">${esc(d.por_dia[0]?.ultima_lista_llegada ?? '—')}</td></tr></table>
        <h2>Por camarera</h2><table><tr><th>Camarera</th><th>Hechas</th><th>Créditos</th><th>Min/crédito</th><th>Aprobadas 1ª</th><th>Re-limpiezas</th></tr>
        ${d.por_camarera.map((c) => `<tr><td>${esc(c.nombre)}</td><td class="n">${c.terminadas}/${c.tareas}</td><td class="n">${c.creditos}</td><td class="n">${c.min_por_credito ?? '—'}</td><td class="n">${c.aprobadas_primera_pct ?? '—'} %</td><td class="n">${c.relimpiezas}</td></tr>`).join('')}</table>
        ${d.defectos.length ? `<h2>Defectos encontrados</h2><table>${d.defectos.map((x) => `<tr><td>${esc(x.texto)}</td><td class="n">${x.veces}</td></tr>`).join('')}</table>` : ''}
        <p class="nota">Uso interno de gestión. No sustituye el procedimiento disciplinario ni autoriza descuentos.</p>`);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const t = k?.totales;
  const tarjeta = (l: string, v: React.ReactNode, sub2?: string) => (
    <div className="rounded-lg border bg-card p-3"><div className="text-xs text-muted-foreground">{l}</div><div className="text-xl font-bold tabular-nums">{v}</div>{sub2 && <div className="text-[11px] text-muted-foreground">{sub2}</div>}</div>
  );
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Desde<input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="text-xs">Hasta<input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        <Button size="sm" variant="outline" onClick={() => void reporteDia()}><Printer className="mr-1 h-4 w-4" /> Reporte diario ({fmtFecha(hasta)})</Button>
        {isFetching && <span className="text-xs text-muted-foreground">Calculando…</span>}
      </div>
      {t && k && <>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
          {tarjeta('Habitaciones limpiadas', t.terminadas, `${t.creditos} créditos`)}
          {tarjeta('Salida', vsEstandar(t.min_salida, k.estandar.salida), `estándar ${k.estandar.salida} min`)}
          {tarjeta('Ocupada', vsEstandar(t.min_ocupada, k.estandar.ocupada), `estándar ${k.estandar.ocupada} min`)}
          {tarjeta('Aprobadas a la primera', t.aprobadas_primera_pct == null ? '—' : `${t.aprobadas_primera_pct} %`, `${t.inspecciones} inspecciones`)}
          {tarjeta('Re-limpiezas', t.relimpiezas, `${t.rapidas} terminadas muy rápido`)}
          {tarjeta('Averías reportadas', t.averias, `${t.no_molestar} con no molestar`)}
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Camarera</th><th className="py-2 pr-2 text-right">Hechas</th><th className="py-2 pr-2 text-right">Créditos</th><th className="py-2 pr-2 text-right">Min/crédito</th><th className="py-2 pr-2 text-right">Aprob. 1ª</th><th className="py-2 pr-2 text-right">Re-limp.</th></tr></thead>
              <tbody>
                {k.por_camarera.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin datos en el rango.</td></tr>}
                {k.por_camarera.map((c, i) => (
                  <tr key={c.nombre} className="border-b last:border-0">
                    <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2">{c.nombre}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{c.terminadas}/{c.tareas}</td><td className="py-1.5 pr-2 text-right tabular-nums">{c.creditos}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{c.min_por_credito ?? '—'}</td><td className="py-1.5 pr-2 text-right tabular-nums">{c.aprobadas_primera_pct == null ? '—' : `${c.aprobadas_primera_pct} %`}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{c.relimpiezas}</td>
                  </tr>
                ))}
              </tbody>
              {k.por_camarera.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={2} className="py-2 pl-3">Total</td>
                <td className="py-2 pr-2 text-right tabular-nums">{t.terminadas}/{t.tareas}</td><td className="py-2 pr-2 text-right tabular-nums">{t.creditos}</td>
                <td colSpan={2} /><td className="py-2 pr-2 text-right tabular-nums">{t.relimpiezas}</td></tr></tfoot>}
            </table>
            <p className="px-3 py-2 text-[11px] text-muted-foreground">Orden alfabético. Uso interno de gestión: no es un ranking ni autoriza descuentos.</p>
          </div>
          <div className="space-y-3">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pl-3 pr-2">Día</th><th className="py-2 pr-2 text-right">Hechas</th><th className="py-2 pr-2 text-right">Créditos</th><th className="py-2 pr-2 text-right">Min salida</th><th className="py-2 pr-2 text-right">Última lista c/ llegada</th></tr></thead>
                <tbody>
                  {k.por_dia.slice(-14).reverse().map((d) => (
                    <tr key={d.fecha} className="border-b last:border-0"><td className="py-1 pl-3 pr-2">{fmtFecha(d.fecha)}</td><td className="py-1 pr-2 text-right tabular-nums">{d.terminadas}/{d.tareas}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{d.creditos}</td><td className="py-1 pr-2 text-right tabular-nums">{d.min_salida ?? '—'}</td>
                      <td className={`py-1 pr-2 text-right tabular-nums ${d.ultima_lista_llegada && d.ultima_lista_llegada > '14:00' ? 'font-semibold text-orange-600' : ''}`}>{d.ultima_lista_llegada ?? '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {k.defectos.length > 0 && (
              <div className="rounded-md border p-2">
                <div className="mb-1 text-xs font-semibold text-muted-foreground">Defectos más frecuentes</div>
                {k.defectos.map((d) => (
                  <div key={d.texto} className="flex items-center gap-2 py-0.5 text-xs">
                    <span className="flex-1">{d.texto}</span>
                    <span className="h-2 rounded bg-red-400" style={{ width: `${Math.min(100, (d.veces / k.defectos[0].veces) * 100)}px` }} />
                    <span className="w-6 text-right tabular-nums">{d.veces}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>}
    </div>
  );
}

// ── Trabajo fuera de la jornada → Nómina ───────────────────────────────────
function Horas() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(sumarDias(hoyISO(), -14));
  const [hasta, setHasta] = useState(hoyISO());
  const [edit, setEdit] = useState<Record<string, { horas: string; tipo: string; motivo: string }>>({});
  const { data = [], isFetching } = useQuery<HoraFila[]>({ queryKey: [QK, 'hk-horas', desde, hasta], queryFn: () => hotelRpc('hk_horas_revisar', { _desde: desde, _hasta: hasta }) });
  const decidir = useMutation({
    mutationFn: (x: { f: HoraFila; decision: 'enviada' | 'descartada' }) => {
      const e = edit[`${x.f.camarera_id}|${x.f.fecha}`];
      return hotelRpc('hk_horas_decidir', { _camarera: x.f.camarera_id, _fecha: x.f.fecha, _decision: x.decision,
        _horas: x.decision === 'enviada' ? Number(e?.horas ?? x.f.horas_sugeridas) : null,
        _tipo: x.decision === 'enviada' ? (e?.tipo ?? x.f.tipo_sugerido) : null, _motivo: e?.motivo ?? null });
    },
    onSuccess: (_r, x) => { toast.success(x.decision === 'enviada' ? 'Enviado a Nómina como novedad del rol' : 'Descartado con su motivo'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pendientes = data.filter((f) => !f.decision);
  return (
    <div className="space-y-3 text-sm">
      <p className="max-w-4xl text-xs text-muted-foreground">
        Se compara el trabajo registrado en housekeeping con la marcación y el turno de Nómina (solo camareras vinculadas a un empleado).
        Lo hecho fuera de la jornada puede corresponder a horas suplementarias (+50 %) o extraordinarias (+100 % en día de descanso):
        usted decide si se envía a Nómina (que valida los topes legales) o se descarta con su motivo. Nada se paga ni se descarta solo.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Desde<input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="text-xs">Hasta<input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        {isFetching && <span className="text-xs text-muted-foreground">Revisando…</span>}
        <span className="text-xs text-muted-foreground">{pendientes.length} por decidir</span>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Fecha</th><th className="py-2 pr-2">Camarera</th><th className="py-2 pr-2">Turno</th>
            <th className="py-2 pr-2">Marcación</th><th className="py-2 pr-2">Limpiezas</th><th className="py-2 pr-2 text-right">Fuera</th><th className="py-2 pr-2">Decisión</th>
          </tr></thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin trabajo fuera de la jornada en el rango ✓</td></tr>}
            {data.map((f, i) => {
              const key = `${f.camarera_id}|${f.fecha}`;
              const e = edit[key] ?? { horas: String(f.horas_sugeridas), tipo: f.tipo_sugerido, motivo: '' };
              const set = (p: Partial<typeof e>) => setEdit({ ...edit, [key]: { ...e, ...p } });
              return (
                <tr key={key} className="border-b align-top last:border-0">
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{fmtFecha(f.fecha)}{f.laborable === false && <div className="text-[11px] text-amber-700">día de descanso</div>}</td>
                  <td className="py-1.5 pr-2">{f.camarera}</td>
                  <td className="py-1.5 pr-2 text-xs">{f.turno ? `${f.turno} ${f.turno_horario ?? ''}` : <span className="text-muted-foreground">sin turno</span>}</td>
                  <td className="py-1.5 pr-2 text-xs">{f.situacion === 'sin_marcacion'
                    ? <span className="font-medium text-red-700 dark:text-red-400">Sin marcación: el proceso de asistencia la marcaría como falta. Regístrela en Nómina.</span>
                    : `${f.entrada ?? '—'} – ${f.salida ?? '—'}`}</td>
                  <td className="py-1.5 pr-2 text-xs">{f.tareas} · {f.primera}–{f.ultima}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{f.minutos} min</td>
                  <td className="py-1.5 pr-2 text-xs">
                    {f.decision ? (
                      <span className={f.decision === 'enviada' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}>
                        {f.decision === 'enviada' ? `Enviada: ${f.horas_enviadas} h` : 'Descartada'}{f.motivo_decision ? ` · ${f.motivo_decision}` : ''}
                      </span>
                    ) : f.minutos >= 10 ? (
                      <div className="flex flex-wrap items-center gap-1">
                        <input className="h-7 w-16 rounded border bg-background px-1 text-right" value={e.horas} onChange={(x) => set({ horas: x.target.value })} title="Horas" />
                        <select className="h-7 rounded border bg-background px-1" value={e.tipo} onChange={(x) => set({ tipo: x.target.value })}>
                          <option value="hora_suplementaria">Suplementaria 50 %</option><option value="hora_extraordinaria">Extraordinaria 100 %</option>
                        </select>
                        <input className="h-7 w-40 rounded border bg-background px-1" placeholder="Motivo" value={e.motivo} onChange={(x) => set({ motivo: x.target.value })} />
                        <Button size="sm" className="h-7 px-2 text-xs" isLoading={decidir.isPending} onClick={() => decidir.mutate({ f, decision: 'enviada' })}>Enviar a Nómina</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={e.motivo.trim().length < 5} onClick={() => decidir.mutate({ f, decision: 'descartada' })}>Descartar</Button>
                      </div>
                    ) : <span className="text-muted-foreground">Revise la marcación en Nómina</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={6} className="py-2 pl-3">Total: {data.length}</td>
            <td className="py-2 pr-2 text-right tabular-nums">{data.reduce((a, f) => a + f.minutos, 0)} min</td><td /></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

// ── Bono ───────────────────────────────────────────────────────────────────
function Bono() {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const [mes, setMes] = useState(hoyISO().slice(0, 7));
  const [montos, setMontos] = useState<Record<string, string>>({});
  const { data: b } = useQuery<BonoResp>({ queryKey: [QK, 'hk-bono', mes], queryFn: () => hotelRpc('hk_bono_calcular', { _mes: mes }) });
  const enviar = useMutation({
    mutationFn: (x: { id: string; monto: number }) => hotelRpc('hk_bono_enviar', { _camarera: x.id, _mes: mes, _monto: x.monto, _nota: null }),
    onSuccess: () => { toast.success('Bono enviado: entra en el próximo rol como ingreso'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const total = useMemo(() => (b?.camareras ?? []).reduce((a, c) => a + Number(c.bono), 0), [b]);
  return (
    <div className="space-y-3 text-sm">
      {dlg.ui}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs">Mes<input type="month" className={`${fieldCls} w-40`} value={mes} onChange={(e) => setMes(e.target.value)} /></label>
        {b && <p className="text-xs text-muted-foreground">
          {b.activo ? `Regla: ${money(b.valor)} por cada crédito sobre ${b.meta} en el mes, si aprueba al menos el ${b.aprobacion_min} % de sus inspecciones.` : 'El bono está desactivado: actívelo en Ajustes.'}
          {' '}Se envía a Nómina como ingreso gravable del próximo rol. Siempre suma: nunca descuenta.
        </p>}
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Camarera</th><th className="py-2 pr-2 text-right">Limpiezas</th><th className="py-2 pr-2 text-right">Créditos</th>
            <th className="py-2 pr-2 text-right">Inspecciones</th><th className="py-2 pr-2 text-right">Aprobadas</th><th className="py-2 pr-2 text-right">Bono calculado</th><th className="py-2 pr-2"></th>
          </tr></thead>
          <tbody>
            {(b?.camareras ?? []).length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin limpiezas en el mes.</td></tr>}
            {(b?.camareras ?? []).map((c, i) => (
              <tr key={c.camarera_id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2">{c.nombre}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{c.tareas}</td><td className="py-1.5 pr-2 text-right tabular-nums">{c.creditos}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{c.inspecciones}</td>
                <td className={`py-1.5 pr-2 text-right tabular-nums ${c.cumple_calidad ? '' : 'text-red-700 dark:text-red-400'}`}>{c.aprobacion_pct == null ? 'sin insp.' : `${c.aprobacion_pct} %`}</td>
                <td className="py-1.5 pr-2 text-right tabular-nums">{money(c.bono)}</td>
                <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                  {c.enviado ? <span className="text-xs text-emerald-700 dark:text-emerald-400">Enviado {money(c.enviado.monto)}</span>
                    : !c.vinculada ? <span className="text-xs text-muted-foreground">Sin ficha de Nómina</span>
                    : <span className="inline-flex gap-1">
                        <input className="h-7 w-20 rounded border bg-background px-1 text-right" value={montos[c.camarera_id] ?? String(c.bono)} onChange={(e) => setMontos({ ...montos, [c.camarera_id]: e.target.value })} />
                        <Button size="sm" className="h-7 px-2 text-xs" disabled={Number(montos[c.camarera_id] ?? c.bono) <= 0} isLoading={enviar.isPending}
                          onClick={async () => { if (await dlg.confirmar(`¿Enviar ${money(Number(montos[c.camarera_id] ?? c.bono))} de bono a ${c.nombre}?`, { detalle: 'Entra como ingreso gravable en el próximo rol. Se envía una sola vez por mes.', aceptar: 'Enviar a Nómina' })) enviar.mutate({ id: c.camarera_id, monto: Number(montos[c.camarera_id] ?? c.bono) }); }}>Enviar a Nómina</Button>
                      </span>}
                </td>
              </tr>
            ))}
          </tbody>
          {(b?.camareras ?? []).length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={6} className="py-2 pl-3">Total</td>
            <td className="py-2 pr-2 text-right tabular-nums">{money(total)}</td><td /></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

// ── Hoja de QR para las puertas ────────────────────────────────────────────
function QrPuertas() {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const { data = [] } = useQuery<{ id: string; numero: string; piso: string | null; tipo: string; qr: string }[]>({ queryKey: [QK, 'hk-qr'], queryFn: () => hotelRpc('hk_qr_listar', {}) });
  const regenerar = useMutation({
    mutationFn: (id: string) => hotelRpc('hk_qr_regenerar', { _hab: id }),
    onSuccess: () => { toast.success('Código nuevo: imprima y reemplace el de esa puerta'); void qc.invalidateQueries({ queryKey: [QK, 'hk-qr'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const imprimir = async (lista = data) => {
    const tarjetas = await Promise.all(lista.map(async (h) => {
      const svg = await QRCode.toString(`K2E-HK:${h.qr}`, { type: 'svg', margin: 1 });
      return `<div class="qr">${svg}<b>${esc(h.numero)}</b><span class="nota">${esc(etiquetaPiso(h.piso))} · escanee para empezar la limpieza</span></div>`;
    }));
    imprimirHtml('QR de puertas', `<h1>QR de puertas · housekeeping</h1><p class="nota">Pegue cada código en el marco interior de la puerta (lado de servicio).</p><div class="grid">${tarjetas.join('')}</div>`);
  };
  return (
    <div className="space-y-3 text-sm">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted-foreground">
          Opcional: con el QR pegado en la puerta, la camarera toca «Escanear puerta» en «Mi día» y la limpieza de esa habitación empieza sola. Prueba que estuvo en la habitación sin rastrear su ubicación.
        </p>
        <Button size="sm" disabled={!data.length} onClick={() => void imprimir()}><Printer className="mr-1 h-4 w-4" /> Imprimir todos ({data.length})</Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {data.map((h) => (
          <div key={h.id} className="flex items-center justify-between rounded border px-2 py-1">
            <span><span className="font-semibold">{h.numero}</span> <span className="text-xs text-muted-foreground">{h.tipo}</span></span>
            <span className="flex gap-1">
              <button type="button" title="Imprimir este" onClick={() => void imprimir([h])}><Printer className="h-3.5 w-3.5" /></button>
              <button type="button" title="Código nuevo (si se perdió o lo copiaron)" onClick={async () => { if (await dlg.confirmar(`¿Generar un código nuevo para la ${h.numero}?`, { detalle: 'El anterior deja de servir: imprima y reemplace el de la puerta.', aceptar: 'Generar código' })) regenerar.mutate(h.id); }}><RefreshCw className="h-3.5 w-3.5" /></button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
